import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom } from 'rxjs';
import { OrderService } from '../../../_services/order.service';
import { AlertService } from '../../../_services/alert.service';

// Badge / header figures. Lost sales fills pending, cancelled and value; sample
// leads fills firstTime and readyToCall.
export interface LostSalesTotals {
  count: number;
  notContacted: number;
  pending?: number;
  cancelled?: number;
  value?: number;
  firstTime?: number;
  readyToCall?: number;
}

export type LeadMode = 'lost' | 'samples';

interface LostSale {
  orderId: number;
  orderNo: string;
  orderDate: string;
  status: string;
  amount: number;
  paymentMethod: string;
  customer: { id: number; name: string; company: string | null; email: string; phone: string | null; type: string };
  agent: string | null;
  itemCount: number;
  items: string[];
  boughtBefore: number;
  lostOrders: number;
  lastFollowUp: string | null;
  lastFollowUpDate: string | null;
  lastFollowUpBy: string | null;
  // Follow-up box state (not from the server).
  noteOpen?: boolean;
  noteText?: string;
  noteSaving?: boolean;
}

// The follow-up list agents work through, in two flavours:
//  - lost sales: customers who started an order but didn't go through with it
//    (still pending, or cancelled);
//  - sample leads: customers who took free samples and haven't ordered since.
// Each is a chance to call, message or email and win them back. A follow-up is
// saved as an order note starting "Follow-up:", so the next agent can see who
// already tried.
@Component({
    selector: 'app-lost-sales',
    imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
    templateUrl: './lost-sales.component.html'
})
export class LostSalesComponent implements OnInit {
  @Input() mode: LeadMode = 'lost';
  @Input() initialDays = 90;
  @Output() closed = new EventEmitter<void>();
  // Fresh badge figures, so the Sales page stays in step with what was done here.
  @Output() totalsChanged = new EventEmitter<LostSalesTotals>();
  // Jump to the other list. The parent swaps which panel is open rather than
  // this component mutating its own `mode`, so the fresh instance resets
  // filters/paging instead of carrying lost-sales state into samples (or vice versa).
  @Output() switchMode = new EventEmitter<LeadMode>();

  days = 90;
  status: 'ALL' | 'PENDING' | 'CANCELLED' = 'ALL';
  contacted: 'ALL' | 'NO' | 'YES' = 'NO';
  search = '';

  items: LostSale[] = [];
  count = 0;
  totals: LostSalesTotals | null = null;
  pageIndex = 0;
  readonly pageSize = 20;
  loading = true;
  loadingMore = false;
  loadError = false;
  exporting = false;
  private token = 0;
  private searchTimer: any;

  get windows(): number[] {
    return this.mode === 'samples' ? [7, 14, 30, 60, 90, 180, 365] : [7, 30, 60, 90, 180, 365];
  }

  windowLabel(d: number): string {
    return d === 365 ? '1y' : d + 'd';
  }

  get isSamples(): boolean {
    return this.mode === 'samples';
  }

  // Days since the order (for samples: since they were sent).
  daysSince(iso: string | null): number {
    return iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : 0;
  }

  constructor(
    private orderService: OrderService,
    private alertService: AlertService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.days = this.initialDays;
    this.load(true);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }

  switchTo(mode: LeadMode): void {
    this.switchMode.emit(mode);
  }

  private fetch(params: any) {
    return this.isSamples ? this.orderService.getSampleLeads(params) : this.orderService.getLostSales(params);
  }

  private request(): any {
    return { days: this.days, status: this.status, contacted: this.contacted, search: this.search.trim(), pageSize: this.pageSize, pageIndex: this.pageIndex };
  }

  load(reset: boolean): void {
    const token = ++this.token;
    if (reset) {
      this.pageIndex = 0;
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.loadError = false;
    this.fetch(this.request()).subscribe({
      next: (r: any) => {
        if (token !== this.token) return;
        this.items = reset ? r.items : [...this.items, ...r.items];
        this.count = r.count;
        this.totals = r.totals;
        this.totalsChanged.emit(r.totals);
        this.loading = false;
        this.loadingMore = false;
      },
      error: (error) => {
        if (token !== this.token) return;
        this.loading = false;
        this.loadingMore = false;
        this.loadError = true;
        this.alertService.alert(error?.error?.message || 'Could not load lost sales', 'error');
      },
    });
  }

  loadMore(): void {
    this.pageIndex++;
    this.load(false);
  }

  get hasMore(): boolean {
    return this.items.length < this.count;
  }

  setDays(d: number): void {
    this.days = d;
    this.load(true);
  }

  setStatus(s: 'ALL' | 'PENDING' | 'CANCELLED'): void {
    this.status = s;
    this.load(true);
  }

  setContacted(c: 'ALL' | 'NO' | 'YES'): void {
    this.contacted = c;
    this.load(true);
  }

  onSearch(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.load(true), 350);
  }

  // ---- display ----
  ago(iso: string | null): string {
    if (!iso) return '';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days < 1) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 31) return `${days} days ago`;
    return `${Math.round(days / 30)} months ago`;
  }

  statusLabel(s: string): string {
    return s === 'PENDING COLLECTION' ? 'Pending collection' : s.charAt(0) + s.slice(1).toLowerCase();
  }

  statusClass(s: string): string {
    return s.startsWith('PENDING') ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700';
  }

  // UK numbers to international form for WhatsApp ("07..." -> "447...").
  waLink(phone: string, o: LostSale): string {
    let d = phone.replace(/[^\d+]/g, '');
    if (d.startsWith('+')) d = d.slice(1);
    else if (d.startsWith('00')) d = d.slice(2);
    else if (d.startsWith('0')) d = '44' + d.slice(1);
    const first = o.customer.name.split(' ')[0];
    const text = this.isSamples
      ? `Hi ${first}, it's Nustone - your free samples should have reached you by now. Did you find one you liked? I can put together a quote whenever you're ready.`
      : `Hi ${first}, it's Nustone - I saw your recent order ${o.orderNo} didn't go through. Can I help you finish it?`;
    return `https://wa.me/${d}?text=${encodeURIComponent(text)}`;
  }

  mailLink(o: LostSale): string {
    const first = o.customer.name.split(' ')[0];
    const subject = this.isSamples ? 'Your Nustone samples' : `Your Nustone order ${o.orderNo}`;
    const body = this.isSamples
      ? `Hi ${first},\n\nYour free samples should have reached you by now - did you find one you liked? I'd be happy to work out how much you'd need and send a quote.\n\nKind regards`
      : `Hi ${first},\n\nI noticed your recent order ${o.orderNo} didn't go through. Is there anything I can help with, or would you like a hand finishing it?\n\nKind regards`;
    return `mailto:${o.customer.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  // ---- actions ----
  openOrder(o: LostSale): void {
    this.closed.emit();
    this.router.navigate(['/sales', o.orderNo]);
  }

  toggleNote(o: LostSale): void {
    o.noteOpen = !o.noteOpen;
    if (o.noteOpen && o.noteText === undefined) o.noteText = '';
  }

  saveNote(o: LostSale): void {
    const text = (o.noteText ?? '').trim();
    if (!text) return;
    o.noteSaving = true;
    this.orderService.createOrderNote({ orderId: o.orderId, orderNote: `Follow-up: ${text}` }).subscribe({
      next: () => {
        o.noteSaving = false;
        o.lastFollowUp = text;
        o.lastFollowUpDate = new Date().toISOString();
        o.lastFollowUpBy = 'You';
        o.noteOpen = false;
        o.noteText = '';
        this.alertService.alert('Follow-up saved on the order', 'success');
        // Refresh the badge (one fewer "not yet contacted").
        this.fetch({ days: this.days, countOnly: true }).subscribe((r: any) =>
          this.totalsChanged.emit({ count: r.count, notContacted: r.notContacted, pending: r.pending, cancelled: r.cancelled, value: r.value, firstTime: r.firstTime, readyToCall: r.readyToCall }),
        );
        if (this.contacted === 'NO') {
          this.items = this.items.filter((x) => x.orderId !== o.orderId);
          this.count = Math.max(0, this.count - 1);
        }
      },
      error: (error) => {
        o.noteSaving = false;
        this.alertService.alert(error?.error?.message || 'Could not save the follow-up', 'error');
      },
    });
  }

  async copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.alertService.alert('Copied', 'success');
    } catch {
      this.alertService.alert('Could not copy', 'error');
    }
  }

  // ---- export ----
  async exportToExcel(): Promise<void> {
    if (this.exporting || !this.count) return;
    this.exporting = true;
    try {
      const rows = await this.fetchAllForExport();
      await this.writeExport(rows);
    } catch {
      this.alertService.alert('Failed to build the export', 'error');
    } finally {
      this.exporting = false;
    }
  }

  // Pages through the current filters at the backend's per-request cap (100)
  // rather than asking for everything in one call - each row runs several
  // correlated subqueries server-side, so one huge pageSize would be slow.
  private async fetchAllForExport(): Promise<LostSale[]> {
    const cap = Math.min(this.count, 2000);
    const pageSize = 100;
    const rows: LostSale[] = [];
    for (let pageIndex = 0; rows.length < cap; pageIndex++) {
      const r: any = await firstValueFrom(this.fetch({ ...this.request(), pageSize, pageIndex }));
      if (!r.items?.length) break;
      rows.push(...r.items);
      if (r.items.length < pageSize) break;
    }
    return rows;
  }

  // exceljs (not xlsx, which can't style cells) so the header row matches
  // the pick-sheet export elsewhere in the app. Loaded on demand - it's
  // large and only needed when someone exports.
  private async writeExport(rows: LostSale[]): Promise<void> {
    const ExcelJS: any = await import('exceljs');
    const Workbook = (ExcelJS.default ?? ExcelJS).Workbook;
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet(this.isSamples ? 'Sample Leads' : 'Lost Sales', { views: [{ state: 'frozen', ySplit: 1 }] });

    sheet.columns = this.isSamples
      ? [
          { header: 'Order Number', key: 'orderNo', width: 14 },
          { header: 'Customer Name', key: 'name', width: 22 },
          { header: 'Company', key: 'company', width: 20 },
          { header: 'Email', key: 'email', width: 30 },
          { header: 'Phone', key: 'phone', width: 16 },
          { header: 'Samples Sent', key: 'orderDate', width: 16 },
          { header: 'Products', key: 'items', width: 40 },
          { header: 'Prior Sample Orders', key: 'lostOrders', width: 16 },
          { header: 'First-Time', key: 'boughtBefore', width: 12 },
          { header: 'Sent By', key: 'agent', width: 16 },
          { header: 'Last Follow-up', key: 'lastFollowUp', width: 30 },
          { header: 'Followed Up By', key: 'lastFollowUpBy', width: 16 },
          { header: 'Followed Up On', key: 'lastFollowUpDate', width: 18 },
        ]
      : [
          { header: 'Order Number', key: 'orderNo', width: 14 },
          { header: 'Customer Name', key: 'name', width: 22 },
          { header: 'Company', key: 'company', width: 20 },
          { header: 'Email', key: 'email', width: 30 },
          { header: 'Phone', key: 'phone', width: 16 },
          { header: 'Order Date', key: 'orderDate', width: 16 },
          { header: 'Status', key: 'status', width: 16 },
          { header: 'Amount', key: 'amount', width: 12 },
          { header: 'Payment Method', key: 'paymentMethod', width: 16 },
          { header: 'Products', key: 'items', width: 40 },
          { header: 'Attempts', key: 'lostOrders', width: 12 },
          { header: 'Bought Before', key: 'boughtBefore', width: 14 },
          { header: 'Taken By', key: 'agent', width: 16 },
          { header: 'Last Follow-up', key: 'lastFollowUp', width: 30 },
          { header: 'Followed Up By', key: 'lastFollowUpBy', width: 16 },
          { header: 'Followed Up On', key: 'lastFollowUpDate', width: 18 },
        ];

    const header = sheet.getRow(1);
    header.height = 22;
    header.eachCell((cell: any) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF171717' } };
      cell.alignment = { vertical: 'middle' };
    });

    for (const o of rows) {
      const row = sheet.addRow({
        orderNo: o.orderNo,
        name: o.customer.name,
        company: o.customer.company || '',
        email: o.customer.email,
        phone: o.customer.phone || '',
        orderDate: o.orderDate ? formatDate(o.orderDate, 'dd MMM yyyy', 'en') : '',
        status: this.statusLabel(o.status),
        amount: o.amount,
        paymentMethod: o.paymentMethod,
        items: o.items.join(', '),
        lostOrders: o.lostOrders,
        boughtBefore: o.boughtBefore,
        agent: o.agent || '',
        lastFollowUp: o.lastFollowUp || '',
        lastFollowUpBy: o.lastFollowUpBy || '',
        lastFollowUpDate: o.lastFollowUpDate ? formatDate(o.lastFollowUpDate, 'dd MMM yyyy', 'en') : '',
      });
      row.eachCell((cell: any) => {
        cell.alignment = { vertical: 'top', wrapText: true };
        cell.border = { bottom: { style: 'thin', color: { argb: 'FFE5E5E5' } } };
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.isSamples ? 'sample-leads' : 'lost-sales'}-${formatDate(new Date(), 'yyyy-MM-dd', 'en')}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
